import React, { useState, useEffect, useMemo, useCallback } from "react";
import { fetchStorages } from "../../../api/inventory/storage";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

/**
 * Modern Storage Selector Component
 * 
 * Features:
 * - Fast storage fetching with debounced search
 * - Single or multiple selection support
 * - Responsive design for mobile and desktop
 * - Compact and user-friendly
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the selector is open
 * @param {Array|Object} props.selectedStorage - Selected storage object(s) - single object for multiple=false, array for multiple=true
 * @param {Function} props.onChange - Callback when selection changes (receives storage object or array)
 * @param {Function} props.onClose - Callback when selector should close
 * @param {boolean} props.multiple - Allow multiple selection (default: false)
 * @param {boolean} props.showHeader - Show header with search (default: true)
 */
export default function ModernStorageSelector({
  open = false,
  selectedStorage = null,
  onChange,
  onClose,
  multiple = false,
  showHeader = true,
  filterVehicle = null, // null = all, true = vehicles only, false = non-vehicles only
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [storages, setStorages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(
    new Set(
      multiple
        ? (Array.isArray(selectedStorage) ? selectedStorage.map((s) => s?.id).filter(Boolean) : [])
        : selectedStorage?.id ? [selectedStorage.id] : []
    )
  );

  // Update selectedIds when selectedStorage prop changes
  useEffect(() => {
    if (open) {
      if (multiple) {
        const ids = Array.isArray(selectedStorage)
          ? selectedStorage.map((s) => s?.id).filter(Boolean)
          : [];
        setSelectedIds(new Set(ids));
      } else {
        setSelectedIds(new Set(selectedStorage?.id ? [selectedStorage.id] : []));
      }
    }
  }, [selectedStorage, open, multiple]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setError(null);
      loadStorages();
    }
  }, [open]);

  // Load storages
  const loadStorages = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchStorages();

      let storagesData = [];
      if (response && Array.isArray(response)) {
        storagesData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        storagesData = response.data;
      }

      // Filter based on filterVehicle parameter
      if (filterVehicle !== null) {
        storagesData = storagesData.filter(storage => {
          if (filterVehicle) {
            // Show only vehicles - must have a vehicle_object
            return storage.vehicle_object && storage.vehicle_object.id;
          } else {
            // Show only non-vehicles - no vehicle_object
            return !storage.vehicle_object || !storage.vehicle_object.id;
          }
        });
      }

      setStorages(storagesData);
    } catch (err) {
      console.error("Error fetching storages:", err);
      setError("Kunne ikke laste lager");
      setStorages([]);
    } finally {
      setIsLoading(false);
    }
  }, [filterVehicle]);

  // Filter storages based on search query
  const filteredStorages = useMemo(() => {
    if (!searchQuery.trim()) {
      return storages;
    }

    const query = searchQuery.toLowerCase().trim();
    return storages.filter((storage) => {
      const title = (storage.title || "").toLowerCase();
      const storageNumber = (storage.storage_number || "").toString().toLowerCase();
      return title.includes(query) || storageNumber.includes(query);
    });
  }, [storages, searchQuery]);

  // Toggle storage selection
  const toggleStorage = useCallback(
    (storage) => {
      const newSelectedIds = new Set(selectedIds);

      if (newSelectedIds.has(storage.id)) {
        newSelectedIds.delete(storage.id);
      } else {
        if (!multiple) {
          newSelectedIds.clear();
        }
        newSelectedIds.add(storage.id);
      }

      setSelectedIds(newSelectedIds);

      // Get selected storages
      const selected = filteredStorages
        .filter((s) => newSelectedIds.has(s.id))
        .map((s) => s);

      if (onChange) {
        if (multiple) {
          onChange(selected);
        } else {
          onChange(selected.length > 0 ? selected[0] : null);
          // Auto-close for single selection
          if (selected.length > 0 && onClose) {
            setTimeout(() => {
              onClose();
            }, 300);
          }
        }
      }
    },
    [selectedIds, filteredStorages, multiple, onChange, onClose]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    if (onChange) {
      onChange(multiple ? [] : null);
    }
  }, [onChange, multiple]);

  // Check if storage is selected
  const isSelected = useCallback(
    (storageId) => {
      return selectedIds.has(storageId);
    },
    [selectedIds]
  );

  if (!open) return null;

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {multiple ? "Velg lager" : "Velg lager"}
            </h3>
            <div className="flex items-center space-x-2">
              {selectedIds.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title="Fjern valg"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title="Lukk"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter lager..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Selected count */}
          {selectedIds.size > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {selectedIds.size} {selectedIds.size === 1 ? "lager" : "lager"} valgt
            </div>
          )}
        </div>
      )}
      
      {!showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter lager..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Storage List */}
      <div className="flex-1 overflow-y-auto max-h-[600px]">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Laster lager...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={loadStorages}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Prøv igjen
              </button>
            </div>
          </div>
        ) : filteredStorages.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-gray-500">
                {searchQuery
                  ? "Ingen lager funnet"
                  : "Ingen lager tilgjengelig"}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredStorages.map((storage) => {
              const selected = isSelected(storage.id);
              return (
                <div
                  key={storage.id}
                  className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                  }`}
                  onClick={() => toggleStorage(storage)}
                >
                  <div className="flex items-center space-x-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          selected
                            ? "bg-blue-600 border-blue-600"
                            : "border-gray-300 hover:border-blue-400"
                        }`}
                      >
                        {selected && (
                          <CheckIcon className="h-4 w-4 text-white" />
                        )}
                      </div>
                    </div>

                    {/* Storage Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {storage.storage_number ? `${storage.storage_number} - ` : ""}
                            {storage.title || `Lager ${storage.id}`}
                          </p>
                          {storage.vehicle && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Kjøretøy: {storage.vehicle.title || storage.vehicle.name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}





